import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  team_release: "Team release", opening_survey_reminder: "Opening survey reminder", exit_survey_reminder: "Exit survey reminder",
  register_nudge: "Register nudge", no_show_followup: "No-show follow-up", admin_notice: "Admin notice",
  session_invite: "Session invite", mentor_digest: "Mentor digest", mentor_notes_digest: "Mentor notes digest",
};
const STATUS_TONE = { sent: "text-emerald-700 bg-emerald-50 border-emerald-200", failed: "text-rose-700 bg-rose-50 border-rose-200", queued: "text-amber-700 bg-amber-50 border-amber-200", sending: "text-sky-700 bg-sky-50 border-sky-200" };
const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function AdminEmailLog() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/email-log"); setRows(data.rows); setCounts(data.counts); }
    catch { toast.error("Could not load email log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const kinds = useMemo(() => [...new Set(rows.map((r) => r.kind))].sort(), [rows]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => (!kind || r.kind === kind) && (!s || [r.to, r.name, r.subject, r.triggered_by].some((v) => (v || "").toLowerCase().includes(s))));
  }, [rows, q, kind]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-5" data-testid="email-log-page">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Email Log</h1>
        <p className="text-sm text-slate-600 mt-1">Every email the platform has sent — who received it, what it said, who triggered it and when.</p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {["sent", "queued", "failed"].map((s) => (
          <span key={s} className={`px-2.5 py-1 rounded-full border text-xs font-semibold ${STATUS_TONE[s]}`} data-testid={`email-log-count-${s}`}>{counts[s] || 0} {s}</span>
        ))}
        <div className="flex-1" />
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="border rounded-md h-9 px-2 text-sm" data-testid="email-log-kind-filter">
          <option value="">All types</option>
          {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] || k}</option>)}
        </select>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient, subject, sender…" className="pl-8 w-72" data-testid="email-log-search" />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="email-log-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      {loading ? <div className="text-sm text-slate-500">Loading…</div> : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="email-log-empty">
          <Mail className="w-6 h-6 mx-auto mb-2 text-slate-400" /> No emails {rows.length ? "match your filter" : "sent yet"}.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
              <tr><th className="px-3 py-2">When</th><th className="px-3 py-2">Recipient</th><th className="px-3 py-2">Subject</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Triggered by</th><th className="px-3 py-2">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`email-log-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmt(r.sent_at || r.created_at)}</td>
                  <td className="px-3 py-2"><div className="font-semibold">{r.name || "—"}</div><div className="font-mono text-xs text-slate-500">{r.to}</div></td>
                  <td className="px-3 py-2 text-slate-700">{r.subject}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{KIND_LABEL[r.kind] || r.kind}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.triggered_by || "system"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold ${STATUS_TONE[r.status] || ""}`} title={r.last_error || ""}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
